import * as React from 'react';
import IconTextButton from '../../custom-components/icon-text-button/IconTextButton';
import IconTextButtonGroup from '../../custom-components/icon-text-button/IconTextButtonGroup';

import { HorizontalAlignLeft as IconAlignLeft }
    from '../../custom-components/svg-icons/align/HorizontalAlignLeft';
import { HorizontalAlignCenter as IconAlignCenter }
    from '../../custom-components/svg-icons/align/HorizontalAlignCenter';
import { HorizontalAlignRight as IconAlignRight }
    from '../../custom-components/svg-icons/align/HorizontalAlignRight';

import IconAdd from 'material-ui/svg-icons/image/add-a-photo';
import IconEdit from 'material-ui/svg-icons/image/photo-filter';
import IconPlus from 'material-ui/svg-icons/content/add';
import IconMinus from 'material-ui/svg-icons/content/remove';
import IconMaximize from 'material-ui/svg-icons/image/photo-size-select-large';
import IconFullwidth from 'material-ui/svg-icons/image/panorama-horizontal';
import IconLink from 'material-ui/svg-icons/editor/insert-link';
import IconGroupSize from 'material-ui/svg-icons/action/aspect-ratio';
import IconCheck from 'material-ui/svg-icons/navigation/check';
import IconResetSize from 'material-ui/svg-icons/notification/sync';

import Badge from 'material-ui/Badge';

import l10n from '../../util/l10n';

interface BaseButtonsProps {
    editEnabled: boolean;
    handleSelect(): void;
    handleEdit(): void;
}
export function getBaseButtons(props: BaseButtonsProps) {
    return (
        <IconTextButtonGroup>
            <IconTextButton
                Icon={<IconAdd />}
                text={l10n('module.image.buttons.addNew')}
                onTouchTap={props.handleSelect}
                dataTestId="image-module-addnew"
                useAlternativeColors={true}
                isPrimary={true}
            />
            <IconTextButton
                Icon={<IconEdit />}
                text={l10n('common.editPicture')}
                onTouchTap={props.handleEdit}
                dataTestId="image-module-effects"
                useAlternativeColors={true}
                isDisabled={!props.editEnabled}
            />
        </IconTextButtonGroup>
    );
}

interface AlignButtonsProps {
    hasAlignCenter: boolean;
    position: number;
    disabled: boolean;
    handler(e: React.SyntheticEvent<{}>, index: number): void;
}
export function getAlignButtons(props: AlignButtonsProps) {
    let index: number;
    let groupIcon: JSX.Element;

    switch (props.position) {
        case 2:
            // right align
            index = props.hasAlignCenter ? 2 : 1;
            groupIcon = <IconAlignRight />;
        break;
        case 3:
            // center align
            index = 1;
            groupIcon = <IconAlignCenter />;
        break;
        default:
            groupIcon = <IconAlignLeft />;
            index = 0;
        break;
    }
    return (
        <IconTextButtonGroup
            isRadio={true}
            selectedIndex={index}
            onChange={props.handler}
            label={l10n('module.image.buttons.groupAlign')}
            Icon={groupIcon}
            dataTestId="image-module-align-group"
        >
            <IconTextButton
                Icon={<IconAlignLeft />}
                text={l10n('module.image.buttons.alignLeft')}
                isDisabled={props.disabled}
                dataTestId="image-module-align-left"
                useAlternativeColors={true}
            />
            {
                props.hasAlignCenter ?
                <IconTextButton
                    Icon={<IconAlignCenter />}
                    text={l10n('module.image.buttons.alignCenter')}
                    isDisabled={props.disabled}
                    dataTestId="image-module-align-center"
                    useAlternativeColors={true}
                />
                : null
            }
            <IconTextButton
                Icon={<IconAlignRight />}
                text={l10n('module.image.buttons.alignRight')}
                isDisabled={props.disabled}
                dataTestId="image-module-align-right"
                useAlternativeColors={true}
            />
        </IconTextButtonGroup>
    );
}

interface ResizeButtonsProps {
    hasFullwidth: boolean;
    minusEnabled: boolean;
    plusEnabled: boolean;
    fitEnabled: boolean;
    fullwidthEnabled: boolean;
    fullwidthPressed: boolean;
    handleMinus(): void;
    handlePlus(): void;
    handleFit(): void;
    handleFullwidth(e: React.SyntheticEvent<{}>, isPressed: boolean): void;
    handleResetSize(): void;
}
export function getResizeButtons(props: ResizeButtonsProps) {
    return (
        <IconTextButtonGroup
            label={l10n('module.image.buttons.groupSize')}
            Icon={<IconGroupSize />}
            dataTestId="image-module-size-group"
        >
            <IconTextButton
                Icon={<IconMinus />}
                text={l10n('module.image.buttons.sizeMinus')}
                onTouchTap={props.handleMinus}
                isDisabled={!props.minusEnabled}
                dataTestId="image-module-size-minus"
                useAlternativeColors={true}
            />
            <IconTextButton
                Icon={<IconPlus />}
                text={l10n('module.image.buttons.sizePlus')}
                onTouchTap={props.handlePlus}
                isDisabled={!props.plusEnabled}
                dataTestId="image-module-size-plus"
                useAlternativeColors={true}
            />
            <IconTextButton
                Icon={<IconMaximize />}
                text={l10n('module.image.buttons.maximize')}
                isDisabled={!props.fitEnabled}
                onTouchTap={props.handleFit}
                dataTestId="image-module-size-maximize"
                useAlternativeColors={true}
            />
            {
                props.hasFullwidth ?
                <IconTextButton
                    Icon={<IconFullwidth />}
                    text={l10n('module.image.buttons.fullwidth')}
                    isDisabled={!props.fullwidthEnabled}
                    isPressable={true}
                    isPressed={props.fullwidthPressed}
                    onTouchTap={props.handleFullwidth}
                    dataTestId="image-module-size-fullwidth"
                    useAlternativeColors={true}
                />
                : null
            }
            <IconTextButton
                Icon={<IconResetSize />}
                text={l10n('module.image.buttons.resetSize')}
                isDisabled={props.fullwidthPressed}
                onTouchTap={props.handleResetSize}
                dataTestId="image-module-size-reset"
                useAlternativeColors={true}
            />
        </IconTextButtonGroup>
    );
}

interface LinkButtonProps {
    hasLink: boolean;
    handler(): void;
}
export function getLinkButton(props: LinkButtonProps) {
    const button = (
        <IconTextButtonGroup>
            <IconTextButton
                Icon={<IconLink />}
                text={l10n('module.image.buttons.link')}
                onTouchTap={props.handler}
                dataTestId="image-module-link"
                useAlternativeColors={true}
            />
        </IconTextButtonGroup>
    );
    if (!props.hasLink) {
        return button;
    } else {
        const style = {
            height: 12,
            margin: 0,
            padding: 0,
            left: '48%',
            top: 17,
            width: 12
        };
        return (
            <Badge
                primary={true}
                badgeContent={<IconCheck color="white" />}
                badgeStyle={style}
                style={{margin: 0, padding: 0}}
            >
                {button}
            </Badge>
        );
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/module/image/Groups.tsx
 **/